// Moving between Olene apps (olene-platform docs/signin.md, "Moving between apps"). The same in
// every app. account.olenesoft.com holds the shared session; one-time tickets carry it here and back.
//
// - Signed out, about to show sign-in: trySso() asks the account site once per tab. Signed in
//   there, you come back signed in here; otherwise you come back to this sign-in page.
// - Just signed in here: linkAccount() takes the session to the account site and comes back.
// - /sso is the landing page for both (handleSsoLanding).
// - Signing out goes through the account site, which signs out of every Olene app.
// Tickets are good once, for 60 s, for this person, this app and this tab (the nonce kept here).

// "tried:<ms>" | "none:<ms>": asked the account site, or it had no session. Only stops asking again for
// a few seconds (no redirect loops); after that a sign-in page asks again, so a session started in
// another app is always picked up.
const FLAG = "olene_sso";
const RETRY_AFTER_MS = 10_000;
const NONCE = "olene_sso_nonce";
const mark = (what) => `${what}:${Date.now()}`;
/** Asked (or signed out) less than minAgeMs ago in this tab. Old unstamped values count as long ago. */
function askedRecently(minAgeMs) {
  const at = Number((store.get(FLAG) ?? "").split(":")[1]);
  return Number.isFinite(at) && Date.now() - at < minAgeMs;
}

/** The account site for this stage, or null where there is none (local development). */
export function hubFor(stage, host = window.location.hostname) {
  if (host === "localhost" || host === "127.0.0.1") return null;
  return stage === "dev" ? "https://account.dev.olenesoft.com" : "https://account.olenesoft.com";
}

/** The olenesoft.com page for an app, where people without access are sent. */
export function productPage(app, stage) {
  return `${stage === "dev" ? "https://dev.olenesoft.com" : "https://olenesoft.com"}/${app}/`;
}

const store = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
  del: (k) => { try { sessionStorage.removeItem(k); } catch { /* private mode */ } },
};

function randomNonce() {
  const b = new Uint8Array(24);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * A full-page "Signing you in…" / "Signing you out…" while moving between apps: the same in every
 * Olene app and on account.olenesoft.com (index.html), so the hops look like one step.
 */
export function showTransition(text = "Signing you in…") {
  if (typeof document === "undefined") return;
  let el = document.getElementById("olene-transition");
  if (!el) {
    const style = document.createElement("style");
    style.textContent = "@keyframes olene-bar{from{transform:translateX(-100%)}to{transform:translateX(250%)}}" +
      "#olene-transition .bar>div{animation:olene-bar 1s ease-in-out infinite}" +
      "@media (prefers-reduced-motion:reduce){#olene-transition .bar>div{animation:none;width:100%}}";
    document.head.appendChild(style);
    el = document.createElement("div");
    el.id = "olene-transition";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.style.cssText = "position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;" +
      "justify-content:center;gap:14px;background:#fafaf9;color:#6b7280;" +
      "font:15px/1.5 Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif";
    el.innerHTML = '<div class="bar" style="width:120px;height:3px;border-radius:3px;background:#e7e5e4;overflow:hidden">' +
      '<div style="width:40%;height:100%;background:#111827;border-radius:3px"></div></div><span></span>';
    document.body.appendChild(el);
  }
  el.querySelector("span").textContent = text;
}

/** Removes the transition (when the move ends inside this app instead of leaving the page). */
export function hideTransition() {
  if (typeof document !== "undefined") document.getElementById("olene-transition")?.remove();
}

/** Signed out: ask the account site for a session (not again within minAgeMs). True if it's navigating away. */
export function trySso(app, hub, minAgeMs = RETRY_AFTER_MS) {
  if (!hub || askedRecently(minAgeMs)) return false;
  const n = randomNonce();
  store.set(FLAG, mark("tried"));
  store.set(NONCE, n);
  showTransition("Signing you in…");
  window.location.replace(`${hub}/sso/start?to=${app}&nonce=${n}&return=${encodeURIComponent(window.location.href)}`);
  return true;
}

/**
 * On a sign-in page: ask again when the person comes back to this tab. They may have signed in to
 * another Olene app in the meantime. Not more than every 30 s. Returns a function that stops it.
 */
export function retrySsoOnReturn(app, hub) {
  const onVisible = () => { if (document.visibilityState === "visible") trySso(app, hub, 30_000); };
  document.addEventListener("visibilitychange", onVisible);
  return () => document.removeEventListener("visibilitychange", onVisible);
}

/** Just signed in: bring the session to the account site, then come back to `dest`. */
export function linkAccount(app, hub, dest = "/") {
  if (!hub) return false;
  store.del(FLAG);
  showTransition("Signing you in…");
  window.location.assign(`${hub}/sso/link?from=${app}&return=${encodeURIComponent(new URL(dest, window.location.origin).href)}`);
  return true;
}

/** Sign out of every Olene app (the account site revokes every session), then back to `returnPath`. */
export function signOutEverywhereUrl(app, hub, returnPath = "/") {
  store.set(FLAG, mark("none"));
  showTransition("Signing you out…");
  const back = new URL(returnPath, window.location.origin).href;
  return hub ? `${hub}/sso/signout?from=${app}&return=${encodeURIComponent(back)}` : back;
}

/** A same-origin path from a return URL, or "/". */
function localPath(r) {
  try {
    const u = new URL(r ?? "/", window.location.origin);
    return u.origin === window.location.origin && !u.pathname.startsWith("/sso") ? u.pathname + u.search : "/";
  } catch { return "/"; }
}

/** The /sso page. opts: { app, hub, accessToken(), ticketSignIn(username, ticket, nonce) }. */
export async function handleSsoLanding(opts) {
  showTransition("Signing you in…");
  const h = new URLSearchParams(window.location.hash.slice(1));
  window.history.replaceState(null, "", window.location.pathname); // tickets don't stay in history
  const to = localPath(h.get("r"));
  const link = h.get("link"), t = h.get("t"), u = h.get("u");

  if (link && opts.hub) {
    // The account site wants this session: a ticket for it, bound to the nonce it gave us.
    try {
      const token = await opts.accessToken();
      if (token) {
        const r = await fetch(`${opts.hub}/api/public/sso/ticket`, {
          method: "POST", headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify({ to: "account", nonce: link }),
        });
        if (r.ok) {
          const { ticket, username } = await r.json();
          window.location.replace(`${opts.hub}/sso/accept#t=${encodeURIComponent(ticket)}&u=${encodeURIComponent(username)}&r=${encodeURIComponent(window.location.origin + to)}`);
          return { kind: "redirected" };
        }
      }
    } catch { /* the app works without it */ }
    hideTransition();
    return { kind: "signedIn", to };
  }
  if (t && u) {
    const n = store.get(NONCE) ?? "";
    store.del(NONCE);
    try {
      await opts.ticketSignIn(u, t, n);
      store.del(FLAG);
      hideTransition();
      return { kind: "signedIn", to };
    } catch {
      store.set(FLAG, mark("none"));
      hideTransition();
      return { kind: "signIn", to };
    }
  }
  store.set(FLAG, mark("none")); // the account site has no session either
  hideTransition();
  return { kind: "signIn", to };
}

/**
 * Signed out anywhere: signed out here too. Signing out of any Olene app revokes every session
 * (GlobalSignOut), but a token already in this tab still looks valid until it expires. So while
 * the tab is visible this asks Cognito (check) when you come back to it and every minute.
 * check resolves false when Cognito refuses the session, true when it's fine, null when it can't
 * tell (offline). Returns a function that stops watching.
 */
export function watchSignOut(check, onSignedOut, everyMs = 60000) {
  let stopped = false;
  let busy = false;
  const run = async () => {
    if (stopped || busy || document.visibilityState !== "visible") return;
    busy = true;
    try {
      if ((await check()) === false && !stopped) {
        stopped = true;
        store.set(FLAG, mark("none")); // don't sign straight back in from the account site
        showTransition("Signing you out…");
        onSignedOut();
      }
    } catch { /* unknown: try again later */ } finally { busy = false; }
  };
  const onVisible = () => { if (document.visibilityState === "visible") run(); };
  const timer = setInterval(run, everyMs);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
  run();
  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", onVisible);
  };
}
